import { useEffect, useMemo, useState } from "react";
import { ColDef } from "ag-grid-community";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import {
  ParkerDetails,
  ParkerInfo,
  ParkerSubInfo,
  SubscriptionParker,
} from "./Data/ApiTypes";
import { PageBar } from "./Controls/PageBar";
import { Button } from "./Controls/Button";
import { DataGrid } from "./Controls/DataGrid";
import { Col, Container, Row } from "react-bootstrap";
import { gridOneCol } from "./Data/Common";
import { Hyperlink } from "./Controls/Hyperlink";
import { Api } from "./Data/Api";
import { processResult } from "./Data/Result";
import { toast } from "react-toastify";
import { ConfirmModal } from "./Controls/ConfirmModal";
import { useHandleNavigation } from "./shared/navigation";
import {
  InviteParkerForm,
  InviteParkerModal,
} from "./UserPlanDetailsPageInviteParkerModal";
import ParkerEditModal from "./ParkerEditModal";
import { parseParkerInfo } from "./Data/ApiParse";
import { SubscriptionStatus } from "./Data/ApiTransport";
 
import { Trash } from "react-bootstrap-icons";

export function ParkersPage() {
  const parkers = useLoaderData() as ParkerDetails;
  const { facilityId, parkerId } = useParams();
  const [allGuestParkers, setAllGuestParkers] = useState<any[]>([]);
  const [showRemove, setShowRemove] = useState(false);
  const [removeParker, setRemoveParker] = useState<ParkerInfo>();
  const [showInvite, setShowInviteParker] = useState(false);
  const [showParkerEditModal, setShowParkerEditModal] = useState(false);
  const [selectedParker, setSelectedParker] = useState<SubscriptionParker>();
  const [selectedParkerSub, setSelectedParkerSub] = useState<ParkerSubInfo>();
  
  const nav = useNavigate();
  const userRole = parkers.staffRole;

  useEffect(() => {
    if (!parkers || !Array.isArray(parkers.subscriptions)) return;

    const guests = parkers.subscriptions.flatMap((sub) =>
      sub.guests.map((guest) => ({
        subscriptionId: sub.subscriptionId,
        sub: sub,
        parkerId: guest.parker.parkerId,
        name: guest.parker.name,
        email: guest.parker.emailAddress,
        lpn:
          guest.vehicles.length > 0
            ? guest.vehicles.map((v) => v.licensePlateNumber).join(" | ")
            : "N/A",
        plan: sub.plan.name,
        planId: sub.plan.planId,
        isManaged: guest.parker.isManaged,
        quantity: guest.quantity,
        parker: guest.parker,
        vehicles: guest.vehicles,
      }))
    );

    setAllGuestParkers(guests);
  }, [parkers]);

  const plans = parkers.subscriptions
    .filter((x) => x.status == SubscriptionStatus.Active)
    .filter(
      (x, index, self) =>
        index ===
        self.findIndex((sub) => sub.subscriptionId === x.subscriptionId)
    )
    .map((x) => ({
      id: x.plan.planId,
      name: x.plan.name,
      subId: x.subscriptionId,
    }));

  function parkerInvite(data: InviteParkerForm) {
    const targetPlan = plans.find((x) => x.id == data.planId);

    Api.subParkerInvite(parseInt(facilityId!), targetPlan!.subId, {
      emailAddress: data.emailAddress,
      name: data.name,
      quantity: 1,
      subscriptionId: targetPlan!.subId,
      isLoginEnabled: data.isLoginEnabled,
    }).then((resp) =>
      processResult(
        resp,
        (val) => {
          setAllGuestParkers([
            ...allGuestParkers,
            {
              subscriptionId: targetPlan!.subId,
              parkerId: val.parker.parkerId,
              name: val.parker.name,
              lpn: "N/A",
              plan: targetPlan!.name,
              email: val.parker.emailAddress,
              quantity: val.quantity,
              isManaged: val.parker.isManaged,
              parker: parseParkerInfo(val.parker),
              vehicles: [],
              sub: {
                subscriptionId: targetPlan?.subId,
                status: SubscriptionStatus.Active,
                facilityHasIntegrationsEnabled: selectedParkerSub?.facilityHasIntegrationsEnabled
              },
            },
          ]);
          
        },
        (str) => {
          
          toast.error(str);
        }
      )
    );
  }

  function parkerRemove(parker: ParkerInfo): void {
    const target = allGuestParkers.find((x) => x.parkerId === parker.parkerId);
    Api.subParkerRemove(parseInt(facilityId!), target.subscriptionId, {
      parkerId: parker.parkerId,
    }).then((resp) =>
      processResult(
        resp,
        (val) => {
          const updated = allGuestParkers.filter((p) =>
            p.parker.parkerId !== parker.parkerId ? true : false
          );
          setAllGuestParkers([...updated]);
          
        },
        (str) => {
          
          toast.error(str);
        }
      )
    );
  }

  const handleNavigation = useHandleNavigation();

  const columns = useMemo<ColDef[]>(
    () => [
      {
        field: "name",
        headerName: "Name",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 120,
        cellRenderer: (params: any) => {
          const name = params.value;

          return (
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Hyperlink
                onClick={() => {
                  setSelectedParker(params.data);
                  setSelectedParkerSub(params.data.sub);
                  setShowParkerEditModal(true);
                }}
              >
                {name}
              </Hyperlink>
            </span>
          );
        },
      },
      {
        field: "managedBy",
        headerName: "Type",
        sortable: true,
        filter: true,
        width: 120,
        maxWidth: 120,
        valueGetter: (params: any) => {
          return params.data.isManaged ? "Self" : "Group";
        },
        cellRenderer: (params: any) => {
          const isManaged = params.data.isManaged;
          return (
            <span
              className={`badge ${isManaged ? "bg-primary" : "bg-secondary"}`}
              style={{ whiteSpace: "nowrap" }}
            >
              {isManaged ? "Self" : "Group"}
            </span>
          );
        },
      },
      {
        field: "email",
        headerName: "Email",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 150,
        valueGetter: (p) => (p.data.isManaged ? p.data.email : "N/A"),
      },
      {
        field: "lpn",
        headerName: "LPN",
        width: 150,
        maxWidth: 150,
        valueGetter: (params) => {
          const vehicles = params.data.vehicles || [];
          return vehicles.length > 0
            ? vehicles.map((v: any) => v.licensePlateNumber).join(" | ")
            : "N/A";
        },
      },
      {
        field: "quantity",
        headerName: "Spots",
        sortable: true,
        filter: true,
        width: 100,
        maxWidth: 100,
        type: "rightAligned",
      },
      {
        field: "plan",
        headerName: "Plan",
        sortable: true,
        filter: true,
        flex: 1,
        minWidth: 120,
        cellRenderer: (params: any) => {
          const plan = params.value;
          const subscriptionId = params.data.subscriptionId;
          const url = `/facility/${facilityId}/parker/${parkerId}/plan/${subscriptionId}`;

          return (
            <a
              onClick={(e) => {
                e.preventDefault();
                handleNavigation(url, {
                  from: "parkersList",
                  parentParkerId: parkerId,
                });
              }}
              href={url}
              style={{
                color: "#d60087",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {plan}
            </a>
          );
        },
      },
      {
        field: "",
        headerName: "",
        sortable: false,
        filter: false,
        width: 80,
        maxWidth: 80,
        cellRenderer: (params: any) => {
          return (
          <button
              className="btn btn-sm"
              title="Delete Parker"
              onClick={() => {
                setRemoveParker(params.data.parker);
                setShowRemove(true);
              }}
              >
            <Trash />
          </button>
          );
        },
      },
    ],
    [facilityId, parkerId, nav]
  );

  return (
    <Container fluid className="h-100">
      <Row className="h-100">
        <Col style={gridOneCol("max-content 1fr 5px")}>
          <div className="d-flex justify-content-between align-items-center mt-2 mb-2">
            <PageBar title="Parkers" />
            <Button
              className="btn-main"
              onClick={() => setShowInviteParker(true)}
            >
              Add Parker
            </Button>
          </div>
          <div style={{ height: "100%", width: "100%" }}>
            <DataGrid
              rowData={[...allGuestParkers].sort((a, b) =>
                a.name.localeCompare(b.name)
              )}
              columnDefs={columns}
            />
          </div>
        </Col>
      </Row>

      <InviteParkerModal
        show={showInvite}
        setShow={setShowInviteParker}
        submit={parkerInvite}
        plans={plans}
      />
      <ConfirmModal
        title="Remove Parker"
        message="Are you sure you want to remove this parker from your plan?"
        show={showRemove}
        setShow={setShowRemove}
        onConfirm={() => {
          if (removeParker !== undefined) {
            parkerRemove(removeParker);
          }
        }}
      />
      {selectedParker && (
        <ParkerEditModal
          show={showParkerEditModal}
          onClose={() => setShowParkerEditModal(false)}
          parker={selectedParker!}
          setParker={setSelectedParker}
          parkers={allGuestParkers}
          setParkers={setAllGuestParkers}
          selectedParkerSub={selectedParkerSub!}
          facilityId={parseInt(facilityId!)}
        />
      )}
    </Container>
  );
}
